import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fadeIn } from '../utils/gsapAnimations';
import Button from '../components/common/Button';
import { createAccount } from '../utils/api';

const validatePassword = (value) => {
  const rules = {
    length: value.length >= 8,
    lowercase: /[a-z]/.test(value),
    uppercase: /[A-Z]/.test(value),
    number: /\d/.test(value),
    symbol: /[^A-Za-z\d]/.test(value),
  };

  return {
    isValid: Object.values(rules).every(Boolean),
    rules,
  };
};

function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const [passwordRules, setPasswordRules] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    symbol: false,
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const pageRef = useRef(null);

  useEffect(() => {
    fadeIn(pageRef.current, 0.4);
  }, []);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    const validation = validatePassword(value);
    setPasswordRules(validation.rules);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setSuccess('');

    if (!name.trim()) {
      setError('Full name is required');
      return;
    }

    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    const validation = validatePassword(password);

    if (!validation.isValid) {
      setError(
        'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.'
      );
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        full_name: name.trim(),
        business_name: name.trim(),
        email: email.trim(),
        telephone: telephone.trim() || null,
        company_id: 1,
        channel: 'website',
        password,
        password_confirmation: passwordConfirmation,
      };

      const response = await createAccount(payload);

      if (response.status === false || response.success === false) {
        setError(response.message || 'Registration failed. Please try again.');
        return;
      }

      setSuccess('Registration successful. Please check your email to confirm your account.');

      setName('');
      setEmail('');
      setTelephone('');
      setPassword('');
      setPasswordConfirmation('');
      setPasswordRules({
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        symbol: false,
      });

      setTimeout(() => {
        navigate('/login');
      }, 1600);
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ passed, children }) => (
    <p className={passed ? 'text-green-600' : 'text-red-500'}>
      {passed ? '✓' : '•'} {children}
    </p>
  );

  return (
    <div
      ref={pageRef}
      className="min-h-screen bg-cream flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100/70">
        <div className="px-8 sm:px-12 py-10 sm:py-14">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-playfair-display font-semibold text-primary mb-3">
              Create Account
            </h2>
            <p className="text-base sm:text-lg font-montserrat text-secondary">
              Join us and start shopping sustainably
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-center text-sm sm:text-base font-montserrat">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-center text-sm sm:text-base font-montserrat">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-base font-montserrat font-medium text-secondary mb-2"
              >
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-300 shadow-sm hover:shadow"
                placeholder="Your name"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-base font-montserrat font-medium text-secondary mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-300 shadow-sm hover:shadow"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="telephone"
                className="block text-base font-montserrat font-medium text-secondary mb-2"
              >
                Phone Number
              </label>
              <input
                id="telephone"
                type="text"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                className="w-full px-5 py-4 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-300 shadow-sm hover:shadow"
                placeholder="080..."
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-base font-montserrat font-medium text-secondary mb-2"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-300 shadow-sm hover:shadow"
                placeholder="Enter password"
              />

              <div className="text-sm mt-3 space-y-1 font-montserrat">
                <Rule passed={passwordRules.length}>At least 8 characters</Rule>
                <Rule passed={passwordRules.lowercase}>Contains lowercase letter</Rule>
                <Rule passed={passwordRules.uppercase}>Contains uppercase letter</Rule>
                <Rule passed={passwordRules.number}>Contains a number</Rule>
                <Rule passed={passwordRules.symbol}>Contains a special character</Rule>
              </div>
            </div>

            <div>
              <label
                htmlFor="passwordConfirmation"
                className="block text-base font-montserrat font-medium text-secondary mb-2"
              >
                Confirm Password
              </label>
              <input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                required
                className="w-full px-5 py-4 border border-gray-300 rounded-xl text-base font-montserrat bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-300 shadow-sm hover:shadow"
                placeholder="Confirm password"
              />

              {passwordConfirmation && password !== passwordConfirmation && (
                <p className="text-sm text-red-500 mt-2 font-montserrat">
                  Passwords do not match.
                </p>
              )}

              {passwordConfirmation && password === passwordConfirmation && (
                <p className="text-sm text-green-600 mt-2 font-montserrat">
                  Passwords match.
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-cream py-4 text-lg font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-base font-montserrat text-secondary">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;